<template>
  <div class="flex flex-wrap col-12 white-bg border-box body-padding">
    <div class="flex col-12 h4">Selectionnez vos entrées:</div>
    <div class="flex col-12 h6 my3">{{ offer.description }}</div>
    <div
      v-for="(price, index) in priceDetails"
      :key="index"
      :class="['col-12 p1', { 'pointer': !isTariffDisabled(price), 'disabled-tariff': isTariffDisabled(price) }]"
    >
      <div class="flex flex-wrap col-12">
        <div class="flex flex-wrap col-8 lg-col-8 md-col-8 sm-col-7">
          <div class="flex flex-wrap items-center col-12">
            <div class="flex col-12">
              <span class="h5 bold sm-hide xs-hide">
                {{ capitalizeFirstLetter(price.label) }}</span>
              <span class="h6 bold lg-hide md-hide">
                {{ capitalizeFirstLetter(price.label) }}</span>
            </div>
          </div>
          <div
            v-if="!isPartnerTariff(price)"
            class="flex col-12 items-center h6"
          >
            <span :class="['bold mr1', { crossed: computePromo(price).canShow }]">
              <span v-if="price.price > 0">
                {{ computeTariffPrice(price) }}€
                <span v-if="isRecurring" class="h5">/ mois</span>
              </span>
              <span v-else> OFFERT </span>
            </span>
            <span v-if="computePromo(price).canShow" class="h5 bold"> {{ (computePromo(price).price /100).toFixed(2) }}€</span>
          </div>
        </div>
        <div
          v-if="showQuantityChanger"
          class="flex col-4 lg-col-4 md-col-4 sm-col-5 h6n bold items-center pl1 justify-end"
        >
          <i
            v-if="showQuantityChanger && priceQuantity(price) > 0 && !isTariffDisabled(price)"
            id="tariff-quantity-minus"
            style="top: 2px"
            class="icon icon-minus h3 pointer"
            @click="handleQuantity(price, -1)"
          ></i>
          <span class="mx1">{{ priceQuantity(price) }}</span>
          <i
            v-if="showQuantityChanger && !isTariffDisabled(price) && (!isPartnerTariff(price) || priceQuantity(price) < 1)"
            id="tariff-quantity-add-more"
            style="top: 2px"
            class="icon icon-add-more-icon h3 pointer"
            @click="handleQuantity(price, 1)"
          ></i>
          <div class="flex ml2" style="min-width: 65px; justify-content: flex-end;">
            <span :style="{ visibility: isPartnerTariff(price) ? 'hidden' : 'visible' }">
              {{ computeTotalPrice(price) }} €
            </span>
          </div>
        </div>
        <div
          v-else
          class="flex col-4 lg-col-4 md-col-4 sm-col-5 h6n bold items-center pl1 justify-end"
        >
          <span
            :class="['flex custom-radio', {'selected-item': isSelected(price), 'pointer': !isTariffDisabled(price)}]"
            @click="!isSelected(price) && !isTariffDisabled(price) ? handleQuantity(price) : undefined"
          ></span>
        </div>
        <div v-if="isPartnerTariff(price) && isSelected(price)" class="flex col-12 mt2">
          <GymlibCodeValidator
            :price="price.negotiatedPrice || price.price"
            :remove-padding="true"
            btn-color="red-bg white"
            @validate-code="validateGymlibCode"
            @gym-lib-code-changed="gymLibCodeChanged"
          />
        </div>
        <div v-if="findPrice(price)" class="flex col-12">
          <div v-if="optionsByType(findPrice(price), optionTypes.MANDATORY).length" class="flex col-12 my1 flex-wrap">
            <span v-if="optionsByType(findPrice(price), optionTypes.OPTIONAL).length" class="bold border-bottom">Obligatoire</span>
            <NewTDAOptionLine
              v-for="(option, index) in optionsByType(findPrice(price), optionTypes.MANDATORY)"
              :key="index"
              :option="option"
              :index="index"
              @manageOption="manageOption"
            />
          </div>

          <!-- OPTIONAL OPTIONS -->
          <div v-if="optionsByType(findPrice(price), optionTypes.OPTIONAL).length" class="flex col-12 my1 flex-wrap">
            <span v-if="optionsByType(findPrice(price), optionTypes.MANDATORY).length" class="bold border-bottom">Suppléments</span>
            <NewTDAOptionLine
              v-for="(option, index) in optionsByType(findPrice(price), optionTypes.OPTIONAL)"
              :key="index"
              :option="option"
              @manageOption="manageOption"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NewTDAOptionLine from '../../components/options/NewTDAOptionLine';
import GymlibCodeValidator from '@/components/tariffs/GymlibCodeValidator';
import { computePromo, computeTariffOptions, computeAvailableOptions } from '@/utils/logics';
import { capitalizeFirstLetter, optionTypes } from '@/utils/utils';

export default {
  components: { NewTDAOptionLine, GymlibCodeValidator },
  data () {
    return {
      siteInfo: this.$store.state.siteInfo,
      contact: this.$store.state.currentParticipant,
      offer: this.$store.state.offerInfo,
      optionTypes: optionTypes,
      gymlibCodeValid: false
    };
  },
  computed: {
    showQuantityChanger () {
      const disabled_features = this.siteInfo.tda_settings.disabled_features || [];
      return !disabled_features.includes('participant-quantity-changer');
    },
    cartItem () {
      return this.$store.state.cartItem;
    },
    tariffs () {
      return this.cartItem && this.cartItem.tariffs && this.cartItem.tariffs.length ? this.cartItem.tariffs : [];
    },
    isRecurring () {
      return this.offer.detail.recurring;
    },
    totalQuantity () {
      return this.tariffs.reduce((acc, curr) => acc += curr.quantity, 0);
    },
    priceDetails () {
      const mappedPrices = this.getOfferPrices().map(price => {
        const tariff = this.tariffs.find(t => t.tariffCode === price.tariffCode);
        const quantity = tariff ? tariff.quantity : 0;
        const unitPrice = price?.promo?.price || price.price;
        const totalPrice = parseFloat((unitPrice * quantity) / 100).toFixed(2);
        return {
          ...price,
          quantity,
          totalPrice,
          isSelected: this.isSelected(price)
        };
      });

      // Separate partner and non-partner tariffs, then sort each group by price
      const nonPartnerTariffs = mappedPrices.filter(price => !this.isPartnerTariff(price)).sort((a, b) => a.price - b.price);
      const partnerTariffs = mappedPrices.filter(price => this.isPartnerTariff(price)).sort((a, b) => a.price - b.price);

      // Return non-partner tariffs first, then partner tariffs
      return [...nonPartnerTariffs, ...partnerTariffs];
    },
    hasPartnerTariffSelected () {
      return this.tariffs.some(tariff => {
        const price = this.getOfferPrices().find(p => p.tariffCode === tariff.tariffCode);
        return price && this.isPartnerTariff(price);
      });
    }
  },
  methods: {
    capitalizeFirstLetter,
    computePromo,
    getOfferPrices () {
      return this.offer && this.offer.detail && this.offer.detail.prices ? [...this.offer.detail.prices].sort((a, b) => a.price - b.price) : [];
    },
    computeTariffPrice (price) {
      return parseFloat(price.price / 100).toFixed(2);
    },
    computeTotalPrice (price) {
      const unitPrice = price && price.promo && price.promo.price ? price.promo.price : price.price;
      return parseFloat((unitPrice * this.priceQuantity(price)) / 100).toFixed(2);
    },
    isSelected (price) {
      return this.tariffs.some(tariff => tariff.tariffCode === price.tariffCode);
    },
    priceQuantity (price) {
      const tariff = this.tariffs.find(t => t.tariffCode === price.tariffCode);
      return tariff ? tariff.quantity : 0;
    },
    handleQuantity (price, quantity) {
      // If selecting a partner tariff, clear all other tariffs first and clear promo campaign
      if (this.isPartnerTariff(price) && quantity > 0) {
        this.$store.commit('setCartItem', {
          ...this.cartItem,
          tariffs: []
        });
        this.gymlibCodeValid = false;
        // Clear promo campaign when partner tariff is selected (can't accumulate promos)
        if (this.$store.state.promoCampaign) {
          this.$store.commit('setPromoCampaign', null);
        }
      }

      // If partner tariff is selected and trying to select another tariff, prevent it
      if (this.hasPartnerTariffSelected && !this.isPartnerTariff(price) && quantity > 0) {
        return;
      }

      let tariffs = JSON.parse(JSON.stringify(this.tariffs));
      let tariffIndex = tariffs.findIndex(tariff => tariff.tariffCode === price.tariffCode);
      let tariff = null;

      if (!this.showQuantityChanger) {
        tariff = { ...price, quantity: 1 };
        tariffs = [tariff];
      } else {
        if (tariffIndex === -1) {
          tariff = { ...price, quantity: 0 };
          tariffs.push(tariff);
          tariffIndex = tariffs.length - 1;
        } else {
          tariff = tariffs[tariffIndex];
        }

        // Limit partner tariff quantity to 1
        if (this.isPartnerTariff(price)) {
          if (quantity > 0 && tariff.quantity >= 1) {
            return;
          }
          tariff.quantity = Math.min(tariff.quantity + quantity, 1);
        } else {
          tariff.quantity += quantity;
        }
      }

      if (tariff.quantity <= 0) {
        tariffs.splice(tariffIndex, 1);
        if (this.isPartnerTariff(price)) {
          this.gymlibCodeValid = false;
        }
      } else {
        tariff.availableOptions = computeTariffOptions(computeAvailableOptions(this.offer.options, tariff).filter(option => option && option.price), tariff);
        tariff.options = [];
        tariffs[tariffIndex] = { ...tariff };
      }

      this.$store.commit('setCartItem', {
        ...this.cartItem,
        tariffs: [...tariffs]
      });
    },
    findPrice (price) {
      return this.tariffs.find(t => price.tariffCode === t.tariffCode);
    },
    optionsByType (tariff, type) {
      return (tariff.availableOptions || []).filter(option => option.optionType === type).map(option => {
        const optionIndex = tariff.options.findIndex(o => o.price.tariffCode === option.price.tariffCode);

        if (optionIndex === -1) {
          option.quantity = option.fixedQuantity ? option.quantity : 0;
        } else {
          option.quantity = tariff.options[optionIndex].quantity;
        }

        return option;
      });
    },
    manageOption (option) {
      const tariffs = JSON.parse(JSON.stringify(this.tariffs));
      const tariffIndex = tariffs.findIndex(t => t.tariffCategoryCode === option.price.tariffCategoryCode);
      const tariff = tariffs[tariffIndex];

      let optionIndex = null;

      if (this.isInternalSession) {
        optionIndex = tariff.options.findIndex(o => o.uuid === option.uuid);
      } else {
        optionIndex = tariff.options.findIndex(o => o.price.tariffCode === option.price.tariffCode && o.price.tariffCategoryCode === option.price.tariffCategoryCode);
      }

      if (optionIndex !== -1) {
        // if this event is called and the quantity didn't change then it's to remove
        // if the quantity is zero and the option is optional, the option is removed from the participant's options
        if (option.quantity === tariff.options[optionIndex].quantity || (option.quantity === 0 && option.optionType === this.optionTypes.OPTIONAL)) {
          tariff.options.splice(optionIndex, 1);
        } else {
          // updates the option
          tariff.options[optionIndex] = option;
        }
      } else if (option.quantity > 0) {
        tariff.options.push(option);
      }
      tariffs[tariffIndex] = tariff;
      this.$store.commit('setCartItem', { ...this.cartItem, tariffs });
    },
    isPartnerTariff (price) {
      return price.isGymlibTariff === true;
    },
    isTariffDisabled (price) {
      // Disable non-partner tariffs when a partner tariff is selected
      if (this.hasPartnerTariffSelected && !this.isPartnerTariff(price)) {
        return true;
      }
      return false;
    },
    validateGymlibCode (isValid) {
      this.gymlibCodeValid = isValid;
      // Store validation state in the tariff
      const tariffs = JSON.parse(JSON.stringify(this.tariffs));
      const partnerTariff = tariffs.find(t => {
        const price = this.getOfferPrices().find(p => p.tariffCode === t.tariffCode);
        return price && this.isPartnerTariff(price);
      });
      if (partnerTariff) {
        partnerTariff.gymlibCodeValid = isValid;
        this.$store.commit('setCartItem', { ...this.cartItem, tariffs });
      }
    },
    gymLibCodeChanged (code) {
      // Only store the code if validation was successful
      // This is called after successful validation from GymlibCodeValidator
      if (this.gymlibCodeValid && code) {
        const tariffs = JSON.parse(JSON.stringify(this.tariffs));
        const partnerTariff = tariffs.find(t => {
          const price = this.getOfferPrices().find(p => p.tariffCode === t.tariffCode);
          return price && this.isPartnerTariff(price);
        });
        if (partnerTariff) {
          partnerTariff.gymlibCode = code;
          this.$store.commit('setCartItem', { ...this.cartItem, tariffs });
        }
      }
    }
  }
};
</script>

<style scoped>
.disabled-tariff {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}
</style>
